import { useAuth } from '@/components/auth/authProvider';
import { useGetOrganizationMaintenanceSettingsQuery } from '@/services/organizations';
import { Box, Table } from '@palmetto/palmetto-components';
import { Fragment, useMemo } from 'react';

import { OrganizationMaintenanceSettingsV2Value, ProgramType, ProgramTypeLabels } from 'types';

import { OrganizationNode } from '../../../types/Organizations';
import { getWarrantyRows, WarrantyRow } from '../../../utils/getWarrantyRows';
import {
  formatMaintenanceCadence,
  formatMaintenanceRate,
  formatServiceRate,
  getWarrantyRowContractSkuLines,
} from '../../../utils/warranties';
import { isPalmettoFinanceUser } from '../../auth/RequirePalmettoFinanceUser';

interface OrganizationExtendedWarrantiesProps {
  organization: OrganizationNode | undefined;
}

type TableRow = WarrantyRow & {
  rowId: string;
  maintenanceValue: OrganizationMaintenanceSettingsV2Value | undefined;
};

export const OrganizationExtendedWarranties = ({ organization }: OrganizationExtendedWarrantiesProps) => {
  const { claims } = useAuth();
  const isInternalUser = isPalmettoFinanceUser(claims);

  const rows = useMemo(
    () => getWarrantyRows(organization?.extendedWarrantyDealerInformation, organization?.programTypes),
    [organization?.extendedWarrantyDealerInformation, organization?.programTypes],
  );

  const neededProgramTypes = useMemo(() => new Set(rows.map((row) => row.programType)), [rows]);

  const alias = organization?.alias;
  const hvacQuery = useGetOrganizationMaintenanceSettingsQuery(
    { alias: alias as string, programType: ProgramType.hvac },
    { skip: !alias || !neededProgramTypes.has(ProgramType.hvac) },
  );
  const waterHeaterQuery = useGetOrganizationMaintenanceSettingsQuery(
    { alias: alias as string, programType: ProgramType.waterHeater },
    { skip: !alias || !neededProgramTypes.has(ProgramType.waterHeater) },
  );
  const geothermalQuery = useGetOrganizationMaintenanceSettingsQuery(
    { alias: alias as string, programType: ProgramType.geothermal },
    { skip: !alias || !neededProgramTypes.has(ProgramType.geothermal) },
  );
  const generatorQuery = useGetOrganizationMaintenanceSettingsQuery(
    { alias: alias as string, programType: ProgramType.generator },
    { skip: !alias || !neededProgramTypes.has(ProgramType.generator) },
  );

  const maintenanceByProgramType: Partial<Record<ProgramType, OrganizationMaintenanceSettingsV2Value | undefined>> =
    {
      [ProgramType.hvac]: hvacQuery.data?.value,
      [ProgramType.waterHeater]: waterHeaterQuery.data?.value,
      [ProgramType.geothermal]: geothermalQuery.data?.value,
      [ProgramType.generator]: generatorQuery.data?.value,
    };

  const tableRows: TableRow[] = useMemo(
    () =>
      rows.map((row) => ({
        ...row,
        rowId: `${row.warrantyProvider}-${row.programType}`,
        maintenanceValue: maintenanceByProgramType[row.programType],
      })),
    // maintenanceByProgramType is rebuilt each render from the 4 query results above, so those are
    // the real dependencies rather than the object reference itself.
    [rows, hvacQuery.data, waterHeaterQuery.data, geothermalQuery.data, generatorQuery.data],
  );

  const warrantyColumns = [
    { heading: 'Warranty Provider', dataKey: 'warrantyProvider' },
    {
      heading: 'Program',
      dataKey: 'programType',
      render: (element: any) => ProgramTypeLabels[element as ProgramType] || element,
    },
    { heading: 'Dealer License Number', dataKey: 'dealerLicenseNumber' },
    {
      heading: 'Maintenance Rate',
      dataKey: 'maintenanceValue',
      render: (_element: any, row: any) => formatMaintenanceRate((row as TableRow).maintenanceValue),
    },
    {
      heading: 'Maintenance Cadence',
      dataKey: 'maintenanceValue',
      render: (_element: any, row: any) => formatMaintenanceCadence((row as TableRow).maintenanceValue),
    },
    {
      heading: 'Service Rate',
      dataKey: 'maintenanceValue',
      render: (_element: any, row: any) => formatServiceRate((row as TableRow).maintenanceValue),
    },
    ...(isInternalUser
      ? [
          {
            heading: 'Contract SKU Mappings',
            dataKey: 'contractSkuTermMappings',
            render: (_element: any, row: any) =>
              getWarrantyRowContractSkuLines(row as WarrantyRow).map((line, index) => (
                <Fragment key={line}>
                  {index > 0 && <br />}
                  {line}
                </Fragment>
              )),
          },
        ]
      : []),
    {
      heading: 'Is Preferred Provider?',
      dataKey: 'isPreferredProvider',
      render: (element: any) => (element ? 'Yes' : 'No'),
    },
  ];

  return (
    <Box margin="lg 0 0 0" childGap="md">
      <Box as="h4" fontSize="md" fontWeight="medium">
        Extended Warranty Dealer Information{' '}
        {organization?.extendedWarrantyDealerInformationOrganization && (
          <Box fontSize="xs" fontWeight="light" as="span">
            {`(inherited from ${organization.extendedWarrantyDealerInformationOrganization})`}
          </Box>
        )}
      </Box>
      {tableRows.length ? (
        <Table
          isCompact
          rowKey="rowId"
          columns={warrantyColumns}
          rows={tableRows}
          isScrollable={{
            x: true,
          }}
        />
      ) : (
        'No information on file'
      )}
    </Box>
  );
};
